import {
  Component,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../toolbar-control.tokens';
import { BaseControl, formatUtcTime } from './base-control';

/**
 * Live-slot + history control plugins for the pluggable toolbar host.
 * Streaming operators (AISstream) show a live badge; pull-based operators
 * (Voyage Radar) drive their refresh from the sensor row instead.
 *
 * Icon-only, like the transport: the meaning is in the tooltip, and anything
 * that has to be READ rather than clicked belongs in the sidebar, which is
 * already narrating what is on the map in full sentences.
 */

export { BaseControl };

@Component({
  selector: 'app-live-badge-control',
  template: `
    <span class="tb-live"><span class="tb-live__dot"></span>LIVE</span>`,
})
export class LiveBadgeControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

@Component({
  selector: 'app-history-entry-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.requestHistory()"
            aria-label="View history"
            matTooltip="Replay a past window" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">history</mat-icon>
    </button>`,
})
export class HistoryEntryControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

@Component({
  selector: 'app-date-range-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.requestHistory()"
            aria-label="Change date range"
            matTooltip="Change the playback date range" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">calendar_month</mat-icon>
    </button>`,
})
export class DateRangeControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

/**
 * Registered but not composed into either sensor's bar: Voyage Radar refreshes
 * from the sensor row, where the control can state its cost, and AISstream
 * streams. Kept because the registry says what CAN exist, not what does — a
 * pull-based sensor without a panel row of its own would want this back.
 */
@Component({
  selector: 'app-refresh-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.refresh()"
            [disabled]="(state$ | async)?.loading"
            aria-label="Refresh vessels"
            matTooltip="Refresh vessel positions" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined"
                [class.tb-spin]="(state$ | async)?.loading">refresh</mat-icon>
    </button>`,
})
export class RefreshControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

/** As above: the sidebar's ON MAP bar carries this stamp now. */
@Component({
  selector: 'app-last-updated-control',
  template: `
    <span class="tb-updated" *ngIf="(state$ | async) as s">
      <ng-container *ngIf="s.lastUpdated as u">Updated {{ fmt(u) }}</ng-container>
    </span>`,
})
export class LastUpdatedControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
  fmt(iso: string): string {
    return formatUtcTime(iso);
  }
}

/**
 * Stays on the map even though the sidebar has a Vessel Types section: filtering
 * is something you do WHILE looking at the chart, and the flag dot means the bar
 * can say a filter is active without spending a word on it.
 */
@Component({
  selector: 'app-type-filter-control',
  template: `
    <ng-container *ngIf="state$ | async as s">
      <button #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              [class.tb-btn--flagged]="s.typeFilter.size > 0"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Filter by vessel type"
              [matTooltip]="s.typeFilter.size > 0
                ? 'Hiding ' + s.typeFilter.size + ' of ' + s.availableTypes.length + ' vessel types'
                : 'Filter by vessel type'"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">filter_alt</mat-icon>
      </button>
    </ng-container>

    <!-- Declared outside the *ngIf: the open overlay holds this TemplateRef. -->
    <ng-template #panel>
      <app-toolbar-drawer *ngIf="state$ | async as s"
                          title="Vessel types"
                          [subtitle]="s.typeFilter.size > 0
                            ? s.typeFilter.size + ' of ' + s.availableTypes.length
                            : null"
                          actionLabel="Clear"
                          [actionDisabled]="s.typeFilter.size === 0"
                          (action)="clear()"
                          (close)="drawer.close()">
        <app-vessel-type-filter
          [availableTypes]="s.availableTypes"
          [selected]="s.typeFilter"
          (selectionChange)="ctrl.setTypeFilter($event)">
        </app-vessel-type-filter>
      </app-toolbar-drawer>
    </ng-template>`,
})
export class TypeFilterControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  /** Back to "show everything" — an empty set IS the no-filter state. */
  clear(): void {
    this.ctrl.setTypeFilter(new Set<string>());
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
  }
}

// `app-port-call-control` used to live here as a button calling
// `ctrl.openPortCalls()` — a hook that was assigned nowhere, on a bar with no
// selected vessel to ask about. It is now a real drawer control in
// ./toolbar-port-call-controls.ts, on the slots where a selection exists.

/**
 * Disabled by either load a pull-based sensor can be in: a refresh is about to
 * replace the layer a replay would hide, and a replay already fetching its
 * frames must not have a second one started over it. `playbackLoading` is the
 * honest flag rather than the delayed one — a gate that waits 150ms to close is
 * not a gate.
 */
@Component({
  selector: 'app-playback-history-control',
  template: `
    <ng-container *ngIf="state$ | async as s">
      <button type="button" class="tb-btn" (click)="ctrl.enterHistoryPlayback()"
              [disabled]="!!s.loading || !!s.playbackLoading"
              aria-label="Playback history"
              matTooltip="Replay every saved refresh" matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">play_circle</mat-icon>
      </button>
    </ng-container>`,
})
export class PlaybackHistoryControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}
